import { useAuth } from './use-auth';

export interface PermissionCheck {
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  isEmployee: () => boolean;
  isAdmin: () => boolean;
}

export const usePermissions = (): PermissionCheck => {
  const { user } = useAuth();

  const hasPermission = (permission: string): boolean => {
    if (!user?.permission_names) return false;
    // If permission_names is empty, treat as super admin with all permissions
    if (user.permission_names.length === 0) return true;

    return user.permission_names.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    if (!user?.role_names) return false;

    return user.role_names.includes(role);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user?.permission_names) return false;
    // If permission_names is empty, treat as super admin with all permissions
    if (user.permission_names.length === 0) return true;

    return permissions.some((permission) => user.permission_names.includes(permission));
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user?.role_names) return false;

    return roles.some((role) => user.role_names.includes(role));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user?.permission_names) return false;
    // If permission_names is empty, treat as super admin with all permissions
    if (user.permission_names.length === 0) return true;

    return permissions.every((permission) => user.permission_names.includes(permission));
  };

  const hasAllRoles = (roles: string[]): boolean => {
    if (!user?.role_names) return false;

    return roles.every((role) => user.role_names.includes(role));
  };

  const isEmployee = (): boolean => {
    if (user?.is_employee !== undefined) {
      return user.is_employee;
    }

    if (!user) return false;

    return user?.role_names && user.role_names.length > 0;
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  return {
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    hasAllPermissions,
    hasAllRoles,
    isEmployee,
    isAdmin,
  };
};
